import React, { useState } from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Handshake, Building2, HeartPulse, Briefcase, GraduationCap, CheckCircle, ExternalLink } from 'lucide-react';
export function PartnersPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const handlePartnerSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real implementation, this would submit the form data
    setFormSubmitted(true);
  };
  return <>
      {/* Hero Section */}
      <Section className="py-20" background="offwhite" data-id="element-1679">
        <div className="text-center mb-12" data-id="element-1680">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-1681">
            Partner With Farasha Medical Aid
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-1682">
            Together, we can bridge healthcare gaps and make a lasting impact on
            underserved communities in Egypt.
          </p>
        </div>
        <div className="mt-8 flex justify-center" data-id="element-1683">
          <div className="bg-white p-6 rounded-xl shadow-md max-w-3xl" data-id="element-1684">
            <p className="text-lg font-inter text-neutral-charcoal" data-id="element-1685">
              As a new nonprofit, Farasha Medical Aid is actively seeking
              partners who share our vision of reducing medical waste and
              improving healthcare access. Whether you're a healthcare provider,
              corporation, foundation, or community organization, your
              partnership can help us scale our impact.
            </p>
          </div>
        </div>
      </Section>
      {/* Partnership Types */}
      <Section background="white" data-id="element-1686">
        <div className="mb-12 text-center" data-id="element-1687">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1688">
            Partnership Opportunities
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-1689">
            Explore the various ways your organization can partner with us to
            make a meaningful difference.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1690">
          {/* Healthcare Providers */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md" data-id="element-1691">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-1692">
              <HeartPulse size={24} className="text-white" data-id="element-1693" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1694">
              Healthcare Providers
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-1695">
              Hospitals, clinics, and medical practices can donate unused
              medical supplies, establish regular donation programs, or provide
              expertise and guidance.
            </p>
            <ul className="space-y-2 mb-6" data-id="element-1696">
              <li className="flex items-start" data-id="element-1697">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1698" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1699">
                  Donate surplus medical supplies
                </span>
              </li>
              <li className="flex items-start" data-id="element-1700">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1701" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1702">
                  Set up regular donation programs
                </span>
              </li>
              <li className="flex items-start" data-id="element-1703">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1704" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1705">
                  Provide medical expertise
                </span>
              </li>
            </ul>
            <Button href="#partner-form" variant="secondary" className="w-full" data-id="element-1706">
              Become a Healthcare Partner
            </Button>
          </div>
          {/* Corporate Partners */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md" data-id="element-1707">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-1708">
              <Building2 size={24} className="text-white" data-id="element-1709" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1710">
              Corporate Partners
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-1711">
              Businesses can support our mission through financial sponsorships,
              employee volunteer programs, matching gift initiatives, or in-kind
              donations.
            </p>
            <ul className="space-y-2 mb-6" data-id="element-1712">
              <li className="flex items-start" data-id="element-1713">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1714" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1715">
                  Corporate social responsibility programs
                </span>
              </li>
              <li className="flex items-start" data-id="element-1716">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1717" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1718">
                  Employee volunteer initiatives
                </span>
              </li>
              <li className="flex items-start" data-id="element-1719">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1720" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1721">
                  Matching gift programs
                </span>
              </li>
            </ul>
            <Button href="#partner-form" variant="secondary" className="w-full" data-id="element-1722">
              Become a Corporate Partner
            </Button>
          </div>
          {/* Medical Manufacturers */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md" data-id="element-1723">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-1724">
              <Briefcase size={24} className="text-white" data-id="element-1725" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1726">
              Medical Manufacturers
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-1727">
              Manufacturers can donate overstock, discontinued items, or
              slightly imperfect products that meet quality standards but would
              otherwise be discarded.
            </p>
            <ul className="space-y-2 mb-6" data-id="element-1728">
              <li className="flex items-start" data-id="element-1729">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1730" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1731">
                  Donate overstock or discontinued items
                </span>
              </li>
              <li className="flex items-start" data-id="element-1732">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1733" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1734">
                  Provide shipping or logistical support
                </span>
              </li>
              <li className="flex items-start" data-id="element-1735">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1736" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1737">
                  Sponsor specific shipments
                </span>
              </li>
            </ul>
            <Button href="#partner-form" variant="secondary" className="w-full" data-id="element-1738">
              Become a Manufacturing Partner
            </Button>
          </div>
          {/* Community Organizations */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md" data-id="element-1739">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-1740">
              <Handshake size={24} className="text-white" data-id="element-1741" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1742">
              Community Organizations
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-1743">
              Faith communities, service clubs, and community groups can
              organize drives, raise awareness, or provide volunteer support for
              our mission.
            </p>
            <ul className="space-y-2 mb-6" data-id="element-1744">
              <li className="flex items-start" data-id="element-1745">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1746" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1747">
                  Host medical supply drives
                </span>
              </li>
              <li className="flex items-start" data-id="element-1748">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1749" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1750">
                  Organize fundraising events
                </span>
              </li>
              <li className="flex items-start" data-id="element-1751">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1752" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1753">
                  Provide volunteer groups
                </span>
              </li>
            </ul>
            <Button href="#partner-form" variant="secondary" className="w-full" data-id="element-1754">
              Become a Community Partner
            </Button>
          </div>
          {/* Educational Institutions */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md md:col-span-2 lg:col-span-1" data-id="element-1755">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-1756">
              <GraduationCap size={24} className="text-white" data-id="element-1757" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1758">
              Educational Institutions
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-1759">
              Schools, colleges, and universities can engage students through
              service-learning projects, internships, or research opportunities
              related to our work.
            </p>
            <ul className="space-y-2 mb-6" data-id="element-1760">
              <li className="flex items-start" data-id="element-1761">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1762" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1763">
                  Develop service-learning opportunities
                </span>
              </li>
              <li className="flex items-start" data-id="element-1764">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1765" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1766">
                  Create internship programs
                </span>
              </li>
              <li className="flex items-start" data-id="element-1767">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1768" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1769">
                  Collaborate on research projects
                </span>
              </li>
            </ul>
            <Button href="#partner-form" variant="secondary" className="w-full" data-id="element-1770">
              Become an Educational Partner
            </Button>
          </div>
        </div>
      </Section>
      {/* Partner Benefits */}
      <Section background="sand" data-id="element-1771">
        <div className="mb-12 text-center" data-id="element-1772">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1773">
            Partner Benefits
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-1774">
            When you partner with Farasha Medical Aid, you gain more than just
            the satisfaction of supporting a worthy cause.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-12" data-id="element-1775">
          <div data-id="element-1776">
            <div className="bg-white p-6 rounded-xl shadow-md mb-6" data-id="element-1777">
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1778">
                For All Partners
              </h3>
              <ul className="space-y-3" data-id="element-1779">
                <li className="flex items-start" data-id="element-1780">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1781" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1782">
                    <strong data-id="element-1783">Impact Reports:</strong> Receive detailed reports on
                    how your partnership is making a difference
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1784">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1785" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1786">
                    <strong data-id="element-1787">Recognition:</strong> Acknowledgment on our website,
                    social media, and annual reports
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1788">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1789" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1790">
                    <strong data-id="element-1791">Network:</strong> Connect with other organizations
                    committed to healthcare equity
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1792">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1793" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1794">
                    <strong data-id="element-1795">Storytelling:</strong> Opportunities to share your
                    partnership story on our blog and newsletter
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-1796">
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1797">
                For Healthcare Partners
              </h3>
              <ul className="space-y-3" data-id="element-1798">
                <li className="flex items-start" data-id="element-1799">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1800" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1801">
                    <strong data-id="element-1802">Waste Reduction:</strong> Reduce your organization's
                    environmental footprint
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1803">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1804" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1805">
                    <strong data-id="element-1806">Staff Engagement:</strong> Boost employee morale
                    through meaningful supply recovery programs
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1807">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1808" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1809">
                    <strong data-id="element-1810">Community Impact:</strong> Demonstrate your
                    commitment to global health equity
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div data-id="element-1811">
            <div className="bg-white p-6 rounded-xl shadow-md mb-6" data-id="element-1812">
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1813">
                For Corporate Partners
              </h3>
              <ul className="space-y-3" data-id="element-1814">
                <li className="flex items-start" data-id="element-1815">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1816" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1817">
                    <strong data-id="element-1818">CSR Fulfillment:</strong> Meet corporate social
                    responsibility goals through tangible impact
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1819">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1820" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1821">
                    <strong data-id="element-1822">Employee Engagement:</strong> Offer meaningful
                    volunteer opportunities for your team
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1823">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1824" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1825">
                    <strong data-id="element-1826">Brand Association:</strong> Align your brand with
                    sustainability and healthcare equity
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1827">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1828" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1829">
                    <strong data-id="element-1830">Tax Benefits:</strong> Receive tax deductions for
                    eligible donations
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-1831">
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1832">
                For Community & Educational Partners
              </h3>
              <ul className="space-y-3" data-id="element-1833">
                <li className="flex items-start" data-id="element-1834">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1835" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1836">
                    <strong data-id="element-1837">Educational Resources:</strong> Access to materials
                    about global health equity
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1838">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1839" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1840">
                    <strong data-id="element-1841">Service Opportunities:</strong> Meaningful projects
                    for members or students
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1842">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1843" />
                  <span className="font-inter text-neutral-charcoal" data-id="element-1844">
                    <strong data-id="element-1845">Community Building:</strong> Foster connections
                    around shared values of service
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Section>
      {/* Current Partners */}
      <Section background="white" data-id="element-1846">
        <div className="mb-12 text-center" data-id="element-1847">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1848">
            Our Current Partners
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-1849">
            We're grateful to these organizations who have joined us in our
            mission to bridge healthcare gaps.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-id="element-1850">
          {/* Placeholder for partner logos - would be replaced with actual partner logos */}
          {[1, 2, 3, 4].map(index => <div key={index} className="bg-neutral-offwhite p-6 rounded-xl flex items-center justify-center h-32" data-id="element-1851">
              <span className="font-playfair text-lg text-primary-deep-blue/50" data-id="element-1852">
                Partner Logo
              </span>
            </div>)}
        </div>
        <div className="mt-12 text-center" data-id="element-1853">
          <p className="text-lg font-inter text-neutral-charcoal mb-6" data-id="element-1854">
            Join these forward-thinking organizations in making a difference.
            Your partnership could be featured here next!
          </p>
          <Button href="#partner-form" data-id="element-1855">Become a Partner</Button>
        </div>
      </Section>
      {/* Partner Form */}
      <Section id="partner-form" background="offwhite" data-id="element-1856">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1857">
          <div data-id="element-1858">
            <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1859">
              Start a Partnership Conversation
            </h2>
            <p className="text-lg font-inter text-neutral-charcoal mb-6" data-id="element-1860">
              We're excited to explore how we can work together to make a
              difference. Complete this form to start the conversation, and our
              partnerships team will reach out to discuss possibilities.
            </p>
            <div className="bg-white p-6 rounded-xl shadow-md mb-6" data-id="element-1861">
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1862">
                What to Expect
              </h3>
              <ul className="space-y-2 font-inter text-neutral-charcoal" data-id="element-1863">
                <li className="flex items-start" data-id="element-1864">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1865" />
                  <span data-id="element-1866">
                    Initial consultation to understand your organization's goals
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1867">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1868" />
                  <span data-id="element-1869">
                    Customized partnership proposal based on your interests
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1870">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1871" />
                  <span data-id="element-1872">
                    Clear communication about impact measurement and reporting
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1873">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1874" />
                  <span data-id="element-1875">
                    Ongoing support throughout the partnership journey
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-neutral-sand p-6 rounded-xl" data-id="element-1876">
              <h3 className="font-playfair font-bold text-xl text-primary-deep-blue mb-3" data-id="element-1877">
                Have an Amazon Wish List?
              </h3>
              <p className="font-inter text-neutral-charcoal mb-4" data-id="element-1878">
                One of the easiest ways to partner with us is by purchasing
                items from our Amazon Wish List. These items ship directly to us
                and address our most urgent needs.
              </p>
              <Button className="flex items-center gap-2" onClick={() => window.open('https://www.amazon.com/hz/wishlist/ls/2BKAGMEAOQZHZ?ref_=wl_share', '_blank')} data-id="element-1879">
                Shop Our Wish List <ExternalLink size={16} data-id="element-1880" />
              </Button>
            </div>
          </div>
          <div data-id="element-1881">
            {formSubmitted ? <div className="bg-white p-8 rounded-xl shadow-md text-center" data-id="element-1882">
                <CheckCircle size={48} className="text-primary-royal-blue mx-auto mb-4" data-id="element-1883" />
                <h3 className="text-2xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1884">
                  Thank You!
                </h3>
                <p className="font-inter text-neutral-charcoal mb-4" data-id="element-1885">
                  We've received your partnership inquiry and will be in touch
                  within 2-3 business days to discuss how we can collaborate.
                </p>
                <Button onClick={() => setFormSubmitted(false)} variant="secondary" data-id="element-1886">
                  Submit another inquiry
                </Button>
              </div> : <form onSubmit={handlePartnerSubmit} className="bg-white p-6 rounded-xl shadow-md" data-id="element-1887">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1888">
                  Partnership Inquiry Form
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-1889">
                  <div data-id="element-1890">
                    <label htmlFor="name" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1891">
                      Contact Name*
                    </label>
                    <input type="text" id="name" name="name" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-1892" />
                  </div>
                  <div data-id="element-1893">
                    <label htmlFor="organization" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1894">
                      Organization Name*
                    </label>
                    <input type="text" id="organization" name="organization" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-1895" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-1896">
                  <div data-id="element-1897">
                    <label htmlFor="email" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1898">
                      Email Address*
                    </label>
                    <input type="email" id="email" name="email" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-1899" />
                  </div>
                  <div data-id="element-1900">
                    <label htmlFor="phone" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1901">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" name="phone" className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-1902" />
                  </div>
                </div>
                <div className="mb-4" data-id="element-1903">
                  <label htmlFor="partnerType" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1904">
                    Partnership Type*
                  </label>
                  <select id="partnerType" name="partnerType" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-1905">
                    <option value="" data-id="element-1906">Select an option</option>
                    <option value="healthcare" data-id="element-1907">Healthcare Provider</option>
                    <option value="corporate" data-id="element-1908">Corporate Partner</option>
                    <option value="manufacturer" data-id="element-1909">Medical Manufacturer</option>
                    <option value="community" data-id="element-1910">Community Organization</option>
                    <option value="educational" data-id="element-1911">Educational Institution</option>
                    <option value="other" data-id="element-1912">Other</option>
                  </select>
                </div>
                <div className="mb-4" data-id="element-1913">
                  <label htmlFor="interests" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1914">
                    Areas of Interest* (Select all that apply)
                  </label>
                  <div className="space-y-2" data-id="element-1915">
                    <label className="flex items-center" data-id="element-1916">
                      <input type="checkbox" name="interests" value="supplies" className="mr-2" data-id="element-1917" />
                      <span className="font-inter text-neutral-charcoal" data-id="element-1918">
                        Donating medical supplies
                      </span>
                    </label>
                    <label className="flex items-center" data-id="element-1919">
                      <input type="checkbox" name="interests" value="financial" className="mr-2" data-id="element-1920" />
                      <span className="font-inter text-neutral-charcoal" data-id="element-1921">
                        Financial support/sponsorship
                      </span>
                    </label>
                    <label className="flex items-center" data-id="element-1922">
                      <input type="checkbox" name="interests" value="volunteers" className="mr-2" data-id="element-1923" />
                      <span className="font-inter text-neutral-charcoal" data-id="element-1924">
                        Volunteer engagement
                      </span>
                    </label>
                    <label className="flex items-center" data-id="element-1925">
                      <input type="checkbox" name="interests" value="drives" className="mr-2" data-id="element-1926" />
                      <span className="font-inter text-neutral-charcoal" data-id="element-1927">
                        Hosting supply drives
                      </span>
                    </label>
                    <label className="flex items-center" data-id="element-1928">
                      <input type="checkbox" name="interests" value="other" className="mr-2" data-id="element-1929" />
                      <span className="font-inter text-neutral-charcoal" data-id="element-1930">
                        Other (please specify in message)
                      </span>
                    </label>
                  </div>
                </div>
                <div className="mb-4" data-id="element-1931">
                  <label htmlFor="message" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1932">
                    Message*
                  </label>
                  <textarea id="message" name="message" rows={4} required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" placeholder="Please share more details about your organization and how you'd like to partner with us." data-id="element-1933"></textarea>
                </div>
                <div className="mb-6" data-id="element-1934">
                  <label className="flex items-start" data-id="element-1935">
                    <input type="checkbox" required className="mt-1 mr-2" data-id="element-1936" />
                    <span className="font-inter text-sm text-neutral-charcoal" data-id="element-1937">
                      I consent to receiving emails about partnership
                      opportunities. I understand I can unsubscribe at any time.
                    </span>
                  </label>
                </div>
                <Button type="submit" className="w-full" data-id="element-1938">
                  Submit Inquiry
                </Button>
              </form>}
          </div>
        </div>
      </Section>
      {/* Host a Drive Section */}
      <Section background="white" data-id="element-1939">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1940">
          <div className="rounded-2xl overflow-hidden shadow-lg" data-id="element-1941">
            <img src="https://images.unsplash.com/photo-1593113616828-6f22bca04804?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" alt="Volunteers organizing a medical supply drive" className="w-full h-full object-cover" data-id="element-1942" />
          </div>
          <div data-id="element-1943">
            <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1944">
              Host a Medical Supply Drive
            </h2>
            <p className="text-lg font-inter text-neutral-charcoal mb-6" data-id="element-1945">
              Medical supply drives are a powerful way to engage your community,
              workplace, or school in our mission. Our comprehensive toolkit
              makes it easy to plan and execute a successful drive.
            </p>
            <div className="space-y-4 mb-6" data-id="element-1946">
              <div className="flex items-start" data-id="element-1947">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1948" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1949">
                  Step-by-step planning guide
                </span>
              </div>
              <div className="flex items-start" data-id="element-1950">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1951" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1952">
                  Printable flyers and collection box labels
                </span>
              </div>
              <div className="flex items-start" data-id="element-1953">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1954" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1955">
                  Social media templates and email announcements
                </span>
              </div>
              <div className="flex items-start" data-id="element-1956">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1957" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1958">
                  Accepted items list and collection guidelines
                </span>
              </div>
              <div className="flex items-start" data-id="element-1959">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1960" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1961">
                  Impact statistics to motivate participants
                </span>
              </div>
            </div>
            <Button href="/downloads/host-a-drive-toolkit.pdf" data-id="element-1962">
              Download Drive Toolkit (PDF)
            </Button>
          </div>
        </div>
      </Section>
      {/* CTA Section */}
      <Section background="deep-blue" data-id="element-1963">
        <div className="text-center" data-id="element-1964">
          <h2 className="text-3xl font-playfair font-bold mb-4" data-id="element-1965">
            Ready to make an impact together?
          </h2>
          <p className="text-xl font-inter mb-8 max-w-3xl mx-auto" data-id="element-1966">
            Join our growing network of partners committed to bridging
            healthcare gaps and making a difference in underserved communities.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1967">
            <Button href="#partner-form" size="lg" data-id="element-1968">
              Become a Partner
            </Button>
            <Button href="/get-involved" variant="secondary" size="lg" data-id="element-1969">
              Other Ways to Help
            </Button>
          </div>
        </div>
      </Section>
    </>;
}